import React, {
	type Dispatch,
	type ReactElement,
	type RefObject,
	type SetStateAction,
	useRef,
	useState,
} from 'react';

import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Placement } from '@atlaskit/popper';
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';

import { useIsDisabledContext } from '../../../../controllers/disabled-context';
import { useProductDependencies } from '../../../../controllers/use-product-dependencies';
import { DEFAULT_UNSPLASH_QUERY_WORD, PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD } from '../constants';
import { UnsplashSearchContent } from '../search/content';
import type { UnsplashResult } from '../types';
import { getUnsplashPhotos } from '../utils/unsplash-api';
import type { UnsplashFetchError } from '../utils/use-unsplash';

import { messages } from './messages';

const iconContainerStyles = xcss({
	paddingLeft: 'space.100',
});

export type SelectedImage = {
	altDescription: string;
	downloadLink?: string;
	fullSizeImageUrl: string;
};

export type UnsplashSearchBarProps = {
	analyticsSource: string;
	defaultUnsplashResults: UnsplashResult[] | undefined;
	dropdownPopupComponentRef?: RefObject<HTMLDivElement | null>;
	elementIdPrefix: string;
	handleError: (error: Error) => void;
	handleImageSelection: (props: SelectedImage) => void;
	helperMessage?: string | ReactElement;
	isDisabled?: boolean;
	isPageTitleComponent?: boolean;
	isUploadingImage?: boolean;
	placement?: Placement;
	setDefaultUnsplashResults: Dispatch<SetStateAction<UnsplashResult[] | undefined>>;
	setError: Dispatch<SetStateAction<UnsplashFetchError | undefined>>; //(error: Error | undefined) => void;
	setUnsplashSearchResults: Dispatch<SetStateAction<UnsplashResult[] | undefined>>;
	unsplashSearchFieldId: string;
	unSplashSearchResults: UnsplashResult[] | undefined;
};

export const UnsplashSearchBar = ({
	elementIdPrefix,
	unsplashSearchFieldId,
	handleImageSelection,
	dropdownPopupComponentRef,
	placement = 'bottom',
	analyticsSource,
	isPageTitleComponent = false,
	isUploadingImage = false,
	defaultUnsplashResults,
	setDefaultUnsplashResults,
	unSplashSearchResults,
	setUnsplashSearchResults,
	setError,
	handleError,
}: UnsplashSearchBarProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isDisabled = useIsDisabledContext();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [unsplashResultsCountFromLastAPICall, setUnsplashResultsCountFromLastAPICall] =
		useState<number>(30);

	const searchResultsRef = useRef<HTMLDivElement>(null);

	const pageNumber = useRef<number>(1);

	const textfieldInputValue = useRef<string>('');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { submitError } = useProductDependencies();

	const debouncedQuery = debounce((searchBar) => {
		textfieldInputValue.current = searchBar?.value;
		const defaultQueryWord = isPageTitleComponent
			? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
			: DEFAULT_UNSPLASH_QUERY_WORD;
		const queryKeyword = textfieldInputValue.current || defaultQueryWord;
		setUnsplashSearchResults(Array(30).fill(null));

		void getUnsplashPhotos(queryKeyword, pageNumber.current, submitError, isPageTitleComponent)
			.then((result) => {
				if (!defaultUnsplashResults || defaultUnsplashResults.length === 0) {
					setDefaultUnsplashResults(result);
				}
				setUnsplashResultsCountFromLastAPICall(result.length);
				setUnsplashSearchResults(result);
				setError(undefined);
			})
			.catch((error) => {
				handleError(error);
			});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'typed',
				actionSubject: 'headerImageSearch',
				actionSubjectId: 'headerImageSearchTyped',
				source: analyticsSource,
			},
		}).fire();
	}, 350);

	const handleSearchOnChange = (event: React.FormEvent<HTMLInputElement>) => {
		pageNumber.current = 1;
		debouncedQuery(event.target);
		searchResultsRef.current?.scroll({ top: 0, behavior: 'smooth' });
	};

	const handleChange = () => {
		setIsPopupOpen(true);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (['Enter', ' ', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
			if (event.key === ' ' && !textfieldInputValue.current) {
				event.preventDefault();
			}
			handleChange();
		}
	};

	return (
		<Popup
			autoFocus={false}
			role="dialog"
			onClose={() => setIsPopupOpen(false)}
		content={() => (
			<UnsplashSearchContent
				id={`${elementIdPrefix}-unsplash-popup-content`}
					dropdownPopupComponentRef={dropdownPopupComponentRef}
					unSplashSearchResults={unSplashSearchResults}
					idPrefix={elementIdPrefix}
					handleImageSelection={handleImageSelection}
					loadMoreText={messages.unsplashLoadMoreText}
					queryWord={textfieldInputValue}
					pageNumber={pageNumber}
					setUnsplashSearchResults={setUnsplashSearchResults}
					handleError={handleError}
					searchResultsRef={searchResultsRef}
					unsplashResultsCountFromLastAPICall={unsplashResultsCountFromLastAPICall}
					setUnsplashResultsCountFromLastAPICall={setUnsplashResultsCountFromLastAPICall}
					isPageTitleComponent={isPageTitleComponent}
					analyticsSource={analyticsSource}
				/>
			)}
			isOpen={isPopupOpen}
			placement={placement}
			testId="unsplash-popup"
		trigger={(triggerProps) => (
			<Textfield
				{...triggerProps}
				role="combobox"
				aria-expanded={isPopupOpen}
				aria-controls={`${elementIdPrefix}-unsplash-popup-content`}
				id={unsplashSearchFieldId}
					testId={`${elementIdPrefix}-unsplash-search-bar`}
					onChange={handleSearchOnChange}
					onClick={handleChange}
					onInput={handleChange}
					onKeyDown={(event) => handleKeyDown(event)}
					isDisabled={isUploadingImage || isDisabled}
					elemBeforeInput={
						<Box xcss={iconContainerStyles}>
							<SearchIcon label="search" color="currentColor" />
						</Box>
					}
					placeholder={formatMessage(messages.refinedUnsplashSearchPlaceholderText)}
				/>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-truecompany-hub')}
		/>
	);
};
